import { HelpCircle } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useLanguage } from "@/i18n/LanguageProvider";
import { cn } from "@/lib/utils";

interface InfoTooltipProps {
  /** Optional bold title displayed above the description. */
  title?: string;
  /** Body text. Always required. */
  description: string;
  /** Popover placement relative to the trigger. */
  side?: "top" | "right" | "bottom" | "left";
  /** Popover alignment along the chosen side. */
  align?: "start" | "center" | "end";
  /** Extra classes for the icon button (positioning, spacing). */
  iconClassName?: string;
  /** Icon scale — `sm` (14px) for dense rows, `md` (16px) for module headers. */
  size?: "sm" | "md";
}

export function InfoTooltip({
  title,
  description,
  side = "bottom",
  align = "start",
  iconClassName,
  size = "md",
}: InfoTooltipProps) {
  const { t } = useLanguage();
  const iconSize = size === "sm" ? "w-3.5 h-3.5" : "w-4 h-4";

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          // Absorb clicks/keys before they bubble to a parent button (e.g.,
          // module-card surface in Modules.tsx). Nested-button is avoided
          // because InfoTooltip ships its own <button> via asChild — but the
          // event would still propagate up the DOM and trigger the outer
          // surface's onClick. stopPropagation keeps the tooltip self-
          // contained.
          onClick={(e) => e.stopPropagation()}
          onPointerDown={(e) => e.stopPropagation()}
          className={cn(
            "inline-flex items-center justify-center rounded-full",
            "text-muted-foreground hover:text-foreground transition-colors",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/50",
            iconClassName,
          )}
          aria-label={t("tooltips.aria.moreInfo")}
        >
          <HelpCircle className={iconSize} aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        side={side}
        align={align}
        className="w-72 p-4 border-cyan-500/22"
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <h4 className="font-display text-sm font-semibold text-foreground mb-1.5">
            {title}
          </h4>
        )}
        <p className="font-display text-xs text-muted-foreground leading-relaxed">
          {description}
        </p>
      </PopoverContent>
    </Popover>
  );
}

export default InfoTooltip;
